import React, { useEffect, useMemo, useRef } from "react";
import { Alert, Box, CircularProgress, Drawer, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { type CartItem, getCartTotal, useCart } from "@ecommerce/store";
import { numberFormatter } from "@ecommerce/shared-utils";
import { trackMetric } from "@ecommerce/analytics";

import { LG } from "../../styles/liquidGlass";
import { Icon } from "../liquid/LiquidPrimitives";
import { DrawerHeader } from "./ShoppingCartDrawerHeader";
import { EmptyCart } from "./ShoppingCartEmptyCart";
import { ShoppingCartItem } from "./ShoppingCartItem";
import { useTiendaInfo } from "../../contexts/TiendaInfoContext";
import { buildCartWhatsAppUrl } from "../../utils/whatsapp";

import { AppConfigFields } from "../../types/appConfigEntities";

interface ShoppingCartDrawerProps {
    cartItems: CartItem[];
    isOpen: boolean;
    onClose: () => void;
}

const ShoppingCartDrawer: React.FC<ShoppingCartDrawerProps> = ({ cartItems, isOpen, onClose }) => {
    const navigate = useNavigate();
    const {
        updateQuantity,
        removeFromCart,
        isReconciling,
        reconciliationError,
        reconciliationChanges,
        reconcilePrices,
    } = useCart();
    const tiendaInfo = useTiendaInfo();
    const bitEsCatalogo = tiendaInfo?.esCatalogo ?? false;
    const quantityReconciliationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const total = useMemo(() => getCartTotal(cartItems), [cartItems]);

    useEffect(
        () => () => {
            if (quantityReconciliationTimer.current) {
                clearTimeout(quantityReconciliationTimer.current);
            }
        },
        [],
    );

    const handleUpdateQuantity = async (
        idProduct: string,
        idProductVariant: string,
        newQuantity: number,
    ): Promise<void> => {
        await updateQuantity(idProduct, idProductVariant, newQuantity);

        if (quantityReconciliationTimer.current) {
            clearTimeout(quantityReconciliationTimer.current);
        }

        quantityReconciliationTimer.current = setTimeout(() => {
            quantityReconciliationTimer.current = null;
            void reconcilePrices("CART_MUTATION").catch(() => {
                // El store conserva las líneas y expone el error con opción de reintento.
            });
        }, 350);
    };

    const handleRemoveItem = (idProduct: string, idProductVariant: string) => {
        removeFromCart(idProduct, idProductVariant);
    };

    const handleCheckout = async () => {
        try {
            await reconcilePrices("CHECKOUT_ENTER");
            onClose();
            navigate("/checkout");
        } catch {
            // El store conserva el carrito y expone el error con opción de reintento.
        }
    };

    const handleConsultarWhatsApp = () => {
        const waItems = cartItems.map((item) => ({
            name: item.name ?? item.productName ?? "Producto",
            price: item.price,
            quantity: item.quantity,
            attributes: item.attributes,
        }));
        const url = buildCartWhatsAppUrl(waItems, tiendaInfo?.whatsappNumero);
        const productIds = new Set(
            cartItems
                .map((item) => item.idProduct)
                .filter((productId): productId is string => Boolean(productId)),
        );

        productIds.forEach((productId) => {
            trackMetric("PRODUCT_WHATSAPP", { entityId: productId });
        });
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const hasItems = cartItems.length > 0;
    const minimumAmount = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseAmount] ?? 0;
    const reachesMinimum = total >= minimumAmount;

    const blockReason: string | null = !hasItems
        ? "Tu carrito está vacío"
        : isReconciling
          ? "Estamos verificando precios y disponibilidad"
          : reconciliationError
            ? "No pudimos verificar los precios del carrito"
            : !reachesMinimum
              ? `El monto mínimo de compra es $${minimumAmount.toLocaleString("es-AR")}`
              : null;

    const canCheckout = blockReason === null;

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                className: "lg-strong",
                style: {
                    boxShadow:
                        "-30px 0 80px -20px rgba(60,40,20,0.25), inset 0 1px 0 rgba(255,255,255,0.85)",
                },
            }}
            ModalProps={{
                BackdropProps: {
                    sx: { backgroundColor: "rgba(20,16,12,0.18)", backdropFilter: "blur(2px)" },
                },
            }}
            sx={{
                "& .MuiDrawer-paper": {
                    width: { xs: "calc(100% - 16px)", md: 444 },
                    maxWidth: "calc(100% - 16px)",
                    top: "12px",
                    bottom: "12px",
                    right: "12px",
                    height: "auto",
                    boxSizing: "border-box",
                    color: LG.color.text,
                    borderRadius: "28px !important",
                    display: "flex",
                    flexDirection: "column",
                    px: { xs: 2, sm: 3.5 },
                    py: 3,
                    background: "var(--glass-bg-strong) !important",
                },
                zIndex: (t) => t.zIndex.drawer + 2000,
            }}
        >
            <DrawerHeader onClose={onClose} />

            <Box sx={{ flexGrow: 1, overflow: "auto", height: "100%", mb: 2 }}>
                {!hasItems ? (
                    <EmptyCart />
                ) : (
                    <Box sx={{ mb: 3 }}>
                        {cartItems.map((item, index) => (
                            <ShoppingCartItem
                                key={`${item.idProduct}-${item.idProductVariant}-${index}`}
                                item={item}
                                onUpdateQuantity={handleUpdateQuantity}
                                onRemoveItem={handleRemoveItem}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {hasItems && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {!isReconciling && reconciliationError && (
                        <Alert
                            severity="error"
                            sx={{ mb: 1 }}
                            action={
                                <button
                                    type="button"
                                    className="btn-ghost"
                                    onClick={() => void reconcilePrices("MANUAL").catch(() => {})}
                                >
                                    Reintentar
                                </button>
                            }
                        >
                            {reconciliationError}
                        </Alert>
                    )}

                    {!isReconciling && !reconciliationError && reconciliationChanges.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            {reconciliationChanges.some(
                                (change) => change.type === "PRICE_CHANGED",
                            ) && "Actualizamos uno o más precios. "}
                            {reconciliationChanges.some(
                                (change) => change.type === "QUANTITY_ADJUSTED",
                            ) && "Ajustamos cantidades según el stock vigente. "}
                            {reconciliationChanges.some((change) => change.type === "REMOVED") &&
                                "Quitamos productos que ya no están disponibles."}
                        </Alert>
                    )}

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "10px 4px 4px",
                            borderTop: "1px solid var(--divider)",
                            marginTop: 2,
                        }}
                    >
                        <span style={{ fontSize: 14, fontWeight: 600 }}>Total</span>
                        <span
                            className="mono"
                            aria-live="polite"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                fontSize: 16,
                                fontWeight: 700,
                            }}
                        >
                            {isReconciling && <CircularProgress size={14} />}
                            {numberFormatter.toStringWithAmount(total, "$")}
                        </span>
                    </div>

                    {bitEsCatalogo ? (
                        <button
                            type="button"
                            className="btn-primary"
                            disabled={!canCheckout}
                            onClick={handleConsultarWhatsApp}
                            style={{
                                justifyContent: "center",
                                gap: 10,
                                width: "100%",
                                marginTop: 6,
                                opacity: canCheckout ? 1 : 0.5,
                                cursor: canCheckout ? "pointer" : "not-allowed",
                            }}
                        >
                            <Icon.whatsapp /> Consultar por WhatsApp
                        </button>
                    ) : (
                        <Tooltip
                            title={blockReason ?? ""}
                            disableHoverListener={canCheckout}
                            disableFocusListener={canCheckout}
                            disableTouchListener={canCheckout}
                            arrow
                            placement="top"
                        >
                            <span style={{ display: "block", marginTop: 6 }}>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    disabled={!canCheckout}
                                    onClick={handleCheckout}
                                    style={{
                                        justifyContent: "center",
                                        gap: 10,
                                        width: "100%",
                                        opacity: canCheckout ? 1 : 0.5,
                                        cursor: canCheckout ? "pointer" : "not-allowed",
                                        pointerEvents: canCheckout ? "auto" : "none",
                                    }}
                                >
                                    <Icon.bag /> Iniciar contratación
                                </button>
                            </span>
                        </Tooltip>
                    )}

                    <button
                        type="button"
                        className="btn-ghost"
                        style={{ justifyContent: "center" }}
                        onClick={onClose}
                    >
                        Seguir buscando
                    </button>
                </div>
            )}
        </Drawer>
    );
};

export default ShoppingCartDrawer;
